/**
 * Classification and telemetry for agent run failures.
 *
 * WHY THIS IS ITS OWN MODULE
 * --------------------------
 * Three separate places need to agree on *why* a run failed, and they used to
 * each answer it differently (or not at all):
 *
 *   - the runtime, to know whether a retry is even possible
 *   - the UI, to pick a message the user can act on
 *   - PostHog and Sentry, to make the failure something you can alert on
 *
 * Keeping the answer in one place is what stops those three drifting apart.
 * It was the drift that hurt: an expired session was reported to telemetry as
 * an untyped `message` string and shown to the user as "please try again" — a
 * retry that could never work, on a failure nobody could alert on.
 *
 * WHERE THE STATUS COMES FROM
 * ---------------------------
 * The LangGraph SDK throws an `HTTPError` carrying a numeric `status`, with a
 * message templated as `HTTP <status>: <body>`. The property is authoritative
 * and is read first. The message is only parsed as a fallback, for errors that
 * crossed a boundary (streaming, serialisation) and arrived as a plain `Error`
 * with nothing but their text left.
 * @example
 * const { kind, statusCode } = classifyRunError(error);
 * if (kind === "auth_expired") // refreshing might save this run
 */

import posthog from "posthog-js";

// ========== CLASSIFICATION ==========

/** Why a run failed, at the granularity the runtime and UI act on. */
export type RunErrorKind = "auth_expired" | "corrupted_history" | "other";

/** The outcome of inspecting a run failure. */
export interface RunErrorClassification {
  kind: RunErrorKind;
  /** HTTP status, or null for failures that never reached a response. */
  statusCode: number | null;
}

// Corrupted-history error: unpaired tool_use/tool_result the API rejects.
const CORRUPTED_HISTORY_RE =
  /no tool call found|function call output with call_id|tool_use ids were found without tool_result/i;

// Fallback status parse, for errors reduced to their message text. Anchored
// to the SDK's own `HTTP <status>: <body>` format: a tool failure whose text
// merely mentions "HTTP 401" must not sign the user out.
const HTTP_STATUS_RE = /^HTTP (\d{3}):/;

/**
 * Reduce any thrown value to a string suitable for logging and matching.
 * @param error - The thrown value.
 * @returns A best-effort message.
 * @example
 * errorMessage(new Error("boom")); // "boom"
 */
export const errorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    // JSON.stringify returns undefined for undefined/functions/symbols.
    const serialized = JSON.stringify(error);
    return typeof serialized === "string" ? serialized : String(error);
  } catch {
    return String(error);
  }
};

/**
 * Extract the HTTP status from a run failure.
 * @param error - The thrown value.
 * @returns The status code, or null if the failure never reached HTTP.
 * @example
 * statusCodeOf(Object.assign(new Error("HTTP 401: ..."), { status: 401 })); // 401
 */
const statusCodeOf = (error: unknown): number | null => {
  if (error && typeof error === "object") {
    const { status } = error as { status?: unknown };
    if (typeof status === "number" && Number.isFinite(status)) return status;
  }
  const match = HTTP_STATUS_RE.exec(errorMessage(error));
  return match ? Number(match[1]) : null;
};

/**
 * Work out why a run failed.
 *
 * Only 401 counts as an expired session. A 403 is the deactivated-account
 * path, which no amount of token refreshing will fix, so it must not be routed
 * into the sign-in-again flow.
 * @param error - The thrown value.
 * @returns The failure kind and its HTTP status, if any.
 * @example
 * classifyRunError(error); // { kind: "auth_expired", statusCode: 401 }
 */
export const classifyRunError = (error: unknown): RunErrorClassification => {
  const statusCode = statusCodeOf(error);
  if (statusCode === 401) return { kind: "auth_expired", statusCode };
  if (CORRUPTED_HISTORY_RE.test(errorMessage(error))) {
    return { kind: "corrupted_history", statusCode };
  }
  return { kind: "other", statusCode };
};

/**
 * Whether a run failed because the user's session expired.
 * @param error - The thrown value.
 * @returns True for a 401 from the agent runtime.
 * @example
 * if (isAuthExpiredError(error)) showSignInAgain();
 */
export const isAuthExpiredError = (error: unknown): boolean =>
  classifyRunError(error).kind === "auth_expired";

/**
 * Whether a run failed due to corrupted (unpaired) thread history.
 * @param error - The thrown value.
 * @returns True when the thread can never replay successfully.
 * @example
 * if (isCorruptedHistoryError(error)) offerNewChat();
 */
export const isCorruptedHistoryError = (error: unknown): boolean =>
  classifyRunError(error).kind === "corrupted_history";

// ========== USER-FACING MESSAGE ==========

/** The translation keys this module can select between. */
export type RunErrorMessageKey =
  | "chat.errorSessionExpired"
  | "chat.errorCorruptedHistory"
  | "chat.errorRunFailed";

/** i18n key per failure kind. Keys live under `chat.` in the locale files. */
const MESSAGE_KEY_BY_KIND: Record<RunErrorKind, RunErrorMessageKey> = {
  auth_expired: "chat.errorSessionExpired",
  corrupted_history: "chat.errorCorruptedHistory",
  other: "chat.errorRunFailed"
};

/**
 * Pick the translation key describing a run failure to the user.
 *
 * The generic key tells the user to try again, which is right for a transient
 * failure and actively misleading for an expired session — the retry 401s
 * every time. Routing by kind is what keeps those apart.
 * @param error - The thrown value.
 * @returns An i18n key under `chat.`.
 * @example
 * t(runErrorMessageKey(error));
 */
export const runErrorMessageKey = (error: unknown): RunErrorMessageKey =>
  MESSAGE_KEY_BY_KIND[classifyRunError(error).kind];

// ========== TELEMETRY ==========

/** Identifying context attached to a reported run failure. */
export interface RunErrorContext {
  assistantId: string;
  threadId: string | null;
  runId: string | null;
}

/**
 * Report a run failure to PostHog (the pane has no Sentry).
 *
 * `error_kind` and `status_code` are the point of this: without them the only
 * way to spot an auth failure in PostHog is to substring-match a free-text
 * message, which is too brittle to build an alert on.
 *
 * Every call is best-effort — a telemetry outage must never mask the run
 * failure that is being reported.
 * @param error - The thrown value.
 * @param context - The assistant, thread and run the failure belongs to.
 * @example
 * logRunError(error, { assistantId, threadId, runId });
 */
export const logRunError = (
  error: unknown,
  context: RunErrorContext
): void => {
  const { kind, statusCode } = classifyRunError(error);
  try {
    posthog.capture("agent_run_error", {
      assistant_id: context.assistantId,
      thread_id: context.threadId,
      run_id: context.runId,
      error_kind: kind,
      status_code: statusCode,
      corrupted_history: kind === "corrupted_history",
      message: errorMessage(error)
    });
  } catch {
    /* ignore PostHog failures */
  }
};
